import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import Experience from './Experience/Experience.js'

/**
 * CandleViewer
 * Standalone interactive 3D viewer for the #explore section.
 * Clones the already-loaded candleModel — no extra network request.
 *
 * Features:
 *  - OrbitControls: drag to rotate, scroll to zoom
 *  - Gentle autoRotate when idle; pauses on user interaction, resumes after 2.5s
 *  - IntersectionObserver: render loop only when section is visible
 */
export default class CandleViewer {
    constructor() {
        this.experience = new Experience()
        this.resources  = this.experience.resources

        this.canvas   = document.querySelector('canvas.candle-viewer')
        this.animId   = null
        this.resumeId = null

        if (!this.canvas) return

        this.setup()
        this.observe()
    }

    setup() {
        const rect = this.canvas.getBoundingClientRect()
        const W = rect.width  > 0 ? rect.width  : 480
        const H = rect.height > 0 ? rect.height : 600

        // ── Renderer ──────────────────────────────────────────────
        // No alpha — opaque dark background so main canvas doesn't bleed through
        this.renderer = new THREE.WebGLRenderer({
            canvas:    this.canvas,
            antialias: true,
        })
        this.renderer.setSize(W, H)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this.renderer.toneMapping         = THREE.CineonToneMapping
        this.renderer.toneMappingExposure = 1.6
        this.renderer.shadowMap.enabled   = true
        this.renderer.shadowMap.type      = THREE.PCFSoftShadowMap

        // ── Scene ─────────────────────────────────────────────────
        this.scene = new THREE.Scene()
        this.scene.background = new THREE.Color(0x1a1008)   // matches --bg-deep

        // ── Camera ────────────────────────────────────────────────
        this.camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 100)
        // Model pivot is at base, scale 0.02 → ~2.5 units tall,
        // resting at y ≈ -0.5 → top at y ≈ +2.0 → frame midpoint ≈ y +0.75
        this.camera.position.set(0, 0.75, 4.5)

        // ── Lights ────────────────────────────────────────────────
        this.scene.add(new THREE.AmbientLight(0x5c4a30, 2.0))

        // Key light — warm gold, upper front
        const keyLight = new THREE.DirectionalLight('#aba5a', 3.2)
        keyLight.position.set(3, 6, 4)
        keyLight.castShadow = true
        keyLight.shadow.mapSize.set(1024, 1024)
        keyLight.shadow.normalBias = 0.05
        this.scene.add(keyLight)

        // Rim light — cool lavender from behind, product-photography separation
        const rimLight = new THREE.DirectionalLight('#a8aef0', 1.4)
        rimLight.position.set(-3, 2, -4)
        this.scene.add(rimLight)

        // Soft warm bounce from below
        const fillLight = new THREE.DirectionalLight('#b07030', 0.5)
        fillLight.position.set(0, -4, 2)
        this.scene.add(fillLight)

        // ── Model ─────────────────────────────────────────────────
        const gltf = this.resources.items['candleModel']
        if (gltf) {
            this.model = gltf.scene.clone()
            this.model.scale.set(0.02, 0.02, 0.02)
            this.model.position.set(0, 0.5, 0)

            this.model.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow    = true
                    child.receiveShadow = true
                    if (child.material) child.material.envMapIntensity = 0.5
                }
            })
            this.scene.add(this.model)
        }

        // ── OrbitControls ─────────────────────────────────────────
        this.controls = new OrbitControls(this.camera, this.canvas)
        this.controls.target.set(0, 0.75, 0)
        this.controls.enableDamping   = true
        this.controls.dampingFactor   = 0.05
        this.controls.autoRotate      = true
        this.controls.autoRotateSpeed = 1.2
        this.controls.minDistance     = 2.5
        this.controls.maxDistance     = 9
        this.controls.maxPolarAngle   = Math.PI * 0.8
        this.controls.update()

        // Pause autoRotate on drag, resume 2.5s after release
        this.canvas.addEventListener('pointerdown', () => {
            clearTimeout(this.resumeId)
            this.controls.autoRotate = false
        })
        this.canvas.addEventListener('pointerup', () => {
            this.resumeId = setTimeout(() => {
                this.controls.autoRotate = true
            }, 2500)
        })

        // ── Resize ────────────────────────────────────────────────
        window.addEventListener('resize', () => this.onResize())
    }

    onResize() {
        const W = this.canvas.clientWidth
        const H = this.canvas.clientHeight
        if (!W || !H) return
        this.camera.aspect = W / H
        this.camera.updateProjectionMatrix()
        this.renderer.setSize(W, H)
    }

    observe() {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.start()
            } else {
                this.stop()
            }
        }, { threshold: 0.1 })

        observer.observe(this.canvas.closest('#explore') ?? this.canvas)
    }

    start() {
        if (this.animId) return
        const loop = () => {
            this.controls.update()
            this.renderer.render(this.scene, this.camera)
            this.animId = requestAnimationFrame(loop)
        }
        this.animId = requestAnimationFrame(loop)
    }

    stop() {
        if (this.animId) {
            cancelAnimationFrame(this.animId)
            this.animId = null
        }
    }
}
